import { CheckInStatusEntity } from 'Models/Entities';
import { createContext } from 'react';
import { action, observable, runInAction, makeObservable } from 'mobx';
import { getEventBooking, updateEventBookingCheckedInStatus } from 'Services/Api/_HumanWritten/CheckInService';
import { EventOccurrence, GetEventSeriesWithOccurrences } from 'Services/Api/_HumanWritten/EventSeriesEntityService';
import { getMessageByKey } from 'Stores/LanguageStore';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { sortByFullName } from '../EventCheckInUtils';
import * as EventCheckInUtils from '../EventCheckInUtils';
import { EventDto } from '../EventCheckInEntities/EventDto';
import { EventCheckInBookingOverviewDto } from '../EventCheckInEntities/EventCheckInOverviewDto';
import EventCheckInSocket from 'Views/Components/_HumanWritten/CheckIn/EventCheckIn/EventContext/EventCheckInSocket';

export interface EventCheckInBookingOptions {
	refresh?: boolean;
	triggerAlert?: boolean;

	// Skip sending a request to the server to update the booking (e.g. when only the client needs to update the display)
	skipUpdateServer?: boolean;
}

/** Outcome of the full-page event check-in confirmation view (no edit route for events). */
export type EventCheckInConfirmResult = 'checkin' | 'cancel';

export interface PendingEventConfirmRequest {
	booking: EventCheckInBookingOverviewDto;
	/** Primary action label, e.g. the booking type's actionString. */
	actionLabel: string;
	resolve: (result: EventCheckInConfirmResult) => void;
}

export interface IEventCheckInStore {
	eventId: string;
	eventDetails: EventDto;
	bookings: EventCheckInBookingOverviewDto[];
	showFilters: boolean;
	/**
	 * All occurrences in the same event series (for switching between occurrences).
	 */
	siblingOccurrences: EventOccurrence[];
	/**
	 * The name of the event series (used for display in check-in).
	 */
	eventSeriesName: string;
	/**
	 * True if the loaded ferry trip has not departed based on the trip's departure time OR the staff member has
	 * confirmed that it is ok to board a departed trip. When false, a warning popup will appear when
	 * 'Scan QR code' button is clicked.
	 */
	skipFerryHasDepartedModal: boolean;
	/**
	 * Load the event and booking entities. This is usually only triggered once during start of CheckInPage.
	 * @param id The event id.
	 */
	loadEvent: (id: string) => Promise<void>;
	/**
	 * Load sibling occurrences for the current event series.
	 */
	loadSiblingOccurrences: () => Promise<void>;
	setBookings: (x: EventCheckInBookingOverviewDto[]) => void;
	/**
	 * Update `skipFerryHasDepartedModal` to true so that the popup doesn't appear again.
	 */
	confirmCheckInForDepartedTrip: () => void;

	/**
	 * Whether or not to show the event booking filters sidebar.
	 */
	setShowFilters: (showFilters: boolean) => void;

	getCheckInConnection: () => EventCheckInSocket;

	updateCheckInStatusToManualStatus: (id: string, statusId: string | null) => Promise<void>;
	updateCheckInStatusToConsecutiveStatus: (
		id: string,
		next: boolean,
		options: EventCheckInBookingOptions,
	) => Promise<void>;

	/** Pending booking awaiting the full-page check-in confirmation view (see EventCheckInConfirmModalHost). */
	pendingConfirmRequest: PendingEventConfirmRequest | null;
	requestCheckInConfirmation: (args: {
		booking: EventCheckInBookingOverviewDto;
		actionLabel: string;
	}) => Promise<EventCheckInConfirmResult>;
	resolveCheckInConfirmation: (result: EventCheckInConfirmResult) => void;

	// getCheckInStatusActionsAndCount: (filteredBookings: CheckInBookingOverviewDto[]) => { checkInStatus: CheckInStatusEntity, count: number }[];

	getCheckInStatusesAndCount: (filteredBookings: EventCheckInBookingOverviewDto[]) => {
		checkInStatus: CheckInStatusEntity;
		count: number;
	}[];

	checkInStatuses: CheckInStatusEntity[];

	getLastCheckInStatusId: () => string;

	getCheckInStatusById: (id?: string) => CheckInStatusEntity | undefined | null;
	getCheckInStatusLabel: (id?: string) => string;

	getNextCheckInStatus: (currentStatusId?: string) => CheckInStatusEntity | undefined | null;
	getNextCheckInStatusLabel: (currentStatusId?: string) => string;

	getPreviousCheckInStatuses: (currentStatusId?: string) => CheckInStatusEntity[] | undefined;
	getPreviousCheckInStatusLabel: (currentStatusId?: string) => string;
}

export class EventCheckInStore implements IEventCheckInStore {
	eventId: string;

	bookings: EventCheckInBookingOverviewDto[];

	showFilters: boolean = false;

	eventDetails: EventDto;

	siblingOccurrences: EventOccurrence[] = [];

	eventSeriesName: string = '';

	skipFerryHasDepartedModal: boolean;

	checkInStatuses: CheckInStatusEntity[];

	pendingConfirmRequest: PendingEventConfirmRequest | null = null;

	private checkInSocket: EventCheckInSocket;

	constructor() {
		makeObservable(this, {
			eventId: observable,
			bookings: observable,
			showFilters: observable,
			eventDetails: observable,
			siblingOccurrences: observable,
			eventSeriesName: observable,
			skipFerryHasDepartedModal: observable,
			checkInStatuses: observable,
			pendingConfirmRequest: observable,
			loadEvent: action,
			loadSiblingOccurrences: action,
			setBookings: action,
			setShowFilters: action,
			confirmCheckInForDepartedTrip: action,
			updateCheckInStatusToManualStatus: action,
			updateCheckInStatusToConsecutiveStatus: action,
			updateCheckInStatus: action,
			requestCheckInConfirmation: action,
			resolveCheckInConfirmation: action,
		});
	}

	/**
	 * Show the full-page event check-in confirmation view for a booking and resolve once the staff
	 * member chooses to check in or cancel. Mirrors the ferry flow; events have no edit route so the
	 * view shows only the check-in action and a close (X).
	 */
	requestCheckInConfirmation(args: {
		booking: EventCheckInBookingOverviewDto;
		actionLabel: string;
	}): Promise<EventCheckInConfirmResult> {
		return new Promise<EventCheckInConfirmResult>(resolve => {
			runInAction(() => {
				this.pendingConfirmRequest = { ...args, resolve };
			});
		});
	}

	/** Resolve and clear the pending confirmation request (idempotent). */
	resolveCheckInConfirmation(result: EventCheckInConfirmResult) {
		const request = this.pendingConfirmRequest;
		this.pendingConfirmRequest = null;
		request?.resolve(result);
	}

	public getCheckInConnection() {
		return this.checkInSocket;
	}

	async loadEvent(id: string) {
		this.eventId = id;
		const response = await EventCheckInUtils.fetchEventCheckInData(id);
		if (response) {
			runInAction(() => {
				this.bookings = response.bookingSummaries.filter(x => new EventCheckInBookingOverviewDto(x));
				this.eventDetails = response.eventDetailDto;
				this.skipFerryHasDepartedModal = new Date() > this.eventDetails.startDateTime;

				this.checkInStatuses = response.checkInStatuses;
			});

			this.createCheckInSocket();

			// Load sibling occurrences for switching between occurrences
			await this.loadSiblingOccurrences();
		}
	}

	async loadSiblingOccurrences() {
		if (!this.eventDetails?.eventSeriesId) {
			runInAction(() => {
				this.siblingOccurrences = [];
				this.eventSeriesName = '';
			});
			return;
		}

		try {
			const series = await GetEventSeriesWithOccurrences(this.eventDetails.eventSeriesId);
			runInAction(() => {
				this.siblingOccurrences = series.upcomingOccurrences ?? [];
				this.eventSeriesName = series.name ?? '';
			});
		} catch (error) {
			console.error('Failed to load sibling occurrences:', error);
			runInAction(() => {
				this.siblingOccurrences = [];
				this.eventSeriesName = '';
			});
		}
	}

	setBookings(x: EventCheckInBookingOverviewDto[]) {
		this.bookings = x;
	}

	setShowFilters() {
		this.showFilters = !this.showFilters;
	}

	confirmCheckInForDepartedTrip() {
		this.skipFerryHasDepartedModal = false;
	}

	removeBooking(id: string) {
		this.setBookings(this.bookings.filter(x => x.id !== id));
	}

	private async createCheckInSocket() {
		if (this.checkInSocket) {
			// Do nothing if we already have a socket for this event
			if (this.checkInSocket.getEventId() === this.eventId) {
				return;
			}

			await this.checkInSocket.closeConnection();
		}
		this.checkInSocket = new EventCheckInSocket(this);
	}

	async updateCheckInStatusToManualStatus(
		id: string,
		statusId: string | null,
		options: EventCheckInBookingOptions = {},
	) {
		const { refresh = false, triggerAlert = true, skipUpdateServer = false } = options;
		let booking: EventCheckInBookingOverviewDto;
		let added = false;

		const bookingIndex = this.bookings.findIndex(x => x.id === id);

		if (bookingIndex === -1) {
			//
			// Fetch booking from server and add to list
			//
			const newBooking = await getEventBooking(id);
			added = true;
			booking = new EventCheckInBookingOverviewDto(newBooking.data);
		} else if (refresh) {
			//
			// Refetch same booking for latest data
			//
			const sameBooking = await getEventBooking(id);
			const newBookingData = new EventCheckInBookingOverviewDto(sameBooking.data);
			runInAction(() => {
				this.bookings[bookingIndex] = newBookingData;
			});

			booking = this.bookings[bookingIndex];
		} else {
			//
			// Use existing booking
			//
			booking = this.bookings[bookingIndex];
		}

		await this.updateCheckInStatus(booking, statusId, added, options);
	}

	async updateCheckInStatus(
		booking: EventCheckInBookingOverviewDto,
		statusId: string | null,
		added: boolean,
		options: EventCheckInBookingOptions = {},
	) {
		const { refresh = false, triggerAlert = true, skipUpdateServer = false } = options;

		const currentStatusOrder = this.getCheckInStatusById(booking.checkInStatusId)?.order ?? 0;
		const newStatusOrder = isNullOrUndefined(statusId) ? 0 : (this.getCheckInStatusById(statusId)?.order ?? 0);
		const nextStatus = newStatusOrder > currentStatusOrder;

		// Update checkedIn status if required
		const checkInStatus =
			isNullOrUndefined(statusId) || statusId === 'booked' ? null : this.getCheckInStatusById(statusId);
		booking.checkedIn = checkInStatus?.includedInCapacity ?? false;
		booking.checkInStatusId = statusId === 'booked' || isNullOrUndefined(statusId) ? undefined : statusId;

		// Re-render list
		if (added) {
			this.setBookings([...this.bookings, booking].sort(sortByFullName()));
		} else {
			this.setBookings([...eventCheckInStore.bookings]);
		}

		if (!skipUpdateServer) {
			await updateEventBookingCheckedInStatus({
				bookingId: booking.id,
				ferryTripId: this.eventDetails?.ferryTripId,
				statusId: checkInStatus?.id ?? null,
			});
		}

		if (triggerAlert) {
			EventCheckInUtils.checkInAlert(
				booking,
				this.getCheckInStatusById(booking.checkInStatusId)?.label ?? 'Booked',
				nextStatus,
			);
		}
	}

	async updateCheckInStatusToConsecutiveStatus(id: string, next: boolean, options: EventCheckInBookingOptions) {
		const bookingIndex = this.bookings.findIndex(x => x.id === id);
		const booking = this.bookings[bookingIndex];

		const lastCheckInStatusId = this.getLastCheckInStatusId();

		if (next && lastCheckInStatusId === booking.checkInStatusId) {
			return;
		}

		const checkInStatusId = next
			? this.getNextCheckInStatus(booking.checkInStatusId)?.id
			: this.getPreviousCheckInStatuses(booking.checkInStatusId)?.filter(x => x.default)?.[0]?.id;

		await this.updateCheckInStatus(booking, checkInStatusId ?? booking.checkInStatusId ?? null, false, options);
	}

	public getCheckInStatusesAndCount(filteredBookings: EventCheckInBookingOverviewDto[]): {
		checkInStatus: CheckInStatusEntity;
		count: number;
	}[] {
		const statusList = [];

		for (const status of this.checkInStatuses) {
			statusList.push({
				checkInStatus: status,
				count:
					status.id === 'booked'
						? filteredBookings.filter(x => isNullOrUndefined(x.checkInStatusId)).length
						: filteredBookings.filter(x => x.checkInStatusId === status?.id).length,
			});
		}

		return statusList
			.filter(x => x.checkInStatus.default || x.count > 0)
			.sort((a, b) => (a.checkInStatus.order ?? 0) - (b.checkInStatus.order ?? 0));
	}

	public getCheckInStatusById(id?: string) {
		if (!id) {
			return null;
		}

		return this.checkInStatuses.find(x => x.id === id);
	}

	public getCheckInStatusLabel(id?: string) {
		const status = this.getCheckInStatusById(id);
		return status?.label ?? getMessageByKey('check-in-status__booked-status__label', 'Booked');
	}

	public getNextCheckInStatus(currentStatusId?: string) {
		const currentOrder = this.checkInStatuses?.find(x => x.id === currentStatusId)?.order ?? 0;

		return this.checkInStatuses.filter(x => x.default).find(x => x.order === currentOrder + 1);
	}

	public getNextCheckInStatusLabel(currentStatusId?: string) {
		return this.getNextCheckInStatus(currentStatusId)?.label ?? 'Complete';
	}

	public getPreviousCheckInStatuses(currentStatusId?: string) {
		const currentOrder = this.checkInStatuses.find(x => x.id === currentStatusId)?.order ?? 0;
		return this.checkInStatuses.filter(x => x.order === currentOrder - 1);
	}

	public getPreviousCheckInStatusLabel(currentStatusId?: string): string {
		return (
			this.getPreviousCheckInStatuses(currentStatusId)?.filter(x => x.default)?.[0]?.label ??
			getMessageByKey('check-in-status__booked-status__label', 'Booked')
		);
	}

	public getLastCheckInStatusId() {
		return this.checkInStatuses.sort((a, b) => b.order - a.order)[0].id;
	}
}

/**
 * This should only be used in the root of the component tree that uses CheckInStoreContext.Provider.
 *
 * Otherwise, use useCheckInStore hook to access check-in related data.
 */
export const eventCheckInStore: IEventCheckInStore = new EventCheckInStore();
export const EventCheckInStoreContext = createContext<IEventCheckInStore>(eventCheckInStore);
